import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';

const STEP_LABELS = {
    A_input: 'A – Input validation',
    B_generate_story: 'B – Generate story',
    B1_generate_story: 'B1 – Generate story',
    B2_translate_story: 'B2 – Translate story',
    C_qa_story: 'C – Story QA',
    E_generate_cover: 'E – Generate cover',
    E1_generate_cover: 'E1 – Generate cover',
    E2_translate_cover: 'E2 – Translate cover',
    G_generate_pages: 'G – Generate pages',
    G1_generate_pages: 'G1 – Generate pages',
    G2_translate_back: 'G2 – Translate back cover',
    I_assemble: 'I – Assemble PDF',
};

const STEP_ORDER = [
    'A_input', 'B_generate_story', 'B1_generate_story', 'B2_translate_story',
    'C_qa_story', 'E_generate_cover', 'E1_generate_cover', 'E2_translate_cover',
    'G_generate_pages', 'G1_generate_pages', 'G2_translate_back', 'I_assemble',
];

const CATEGORY_LABELS = {
    vehicle_eyes:        'Vehicle eye placement',
    arms_legs:           'Vehicle arms/legs',
    text_in_image:       'Text visible in image',
    clothing_drift:      'Clothing inconsistency',
    color_drift:         'Colour drift',
    gender_flip:         'Gender inconsistency',
    duplicate_character: 'Duplicate character',
    scene_accuracy:      'Wrong scene/background',
    style_drift:         'Art style drift',
    character_details:   'Character detail error',
    other:               'Other',
};

function fmtSeconds(s) {
    if (s == null) return '–';
    const rounded = Math.round(s);
    if (rounded >= 60) return `${Math.floor(rounded / 60)}m ${rounded % 60}s`;
    return `${rounded}s`;
}

function pct(n, total) {
    if (!total) return '0%';
    return `${Math.round((100 * n) / total)}%`;
}

function AdminMetricsScreen() {
    const navigate = useNavigate();
    const { userInfo } = useSelector(state => state.userLogin);

    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [days, setDays]       = useState(30);

    useEffect(() => {
        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
        }
    }, [navigate, userInfo]);

    useEffect(() => {
        const fetchMetrics = async () => {
            setLoading(true);
            setError(null);
            try {
                const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
                const { data: resp } = await axios.get(
                    `/api/genai/admin/metrics/?days=${days}`,
                    config
                );
                setData(resp);
            } catch (err) {
                setError(err.response?.data?.error || err.message);
            } finally {
                setLoading(false);
            }
        };

        if (userInfo) fetchMetrics();
    }, [userInfo, days]);

    if (loading) return (
        <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading metrics…</p>
        </div>
    );

    if (error) return (
        <div className="alert alert-danger">Error loading metrics: {error}</div>
    );

    if (!data) return null;

    const {
        total_jobs, completed, failed, in_progress,
        avg_step_durations, image_qa, story_qa,
    } = data;

    // Sort steps in canonical order
    const sortedSteps = STEP_ORDER
        .filter(s => avg_step_durations[s] != null)
        .concat(
            Object.keys(avg_step_durations).filter(s => !STEP_ORDER.includes(s))
        );

    // Sort categories by count desc
    const totalCatCount = Object.values(image_qa.category_counts || {}).reduce((a, b) => a + b, 0);
    const sortedCategories = Object.entries(image_qa.category_counts || {})
        .sort(([, a], [, b]) => b - a);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="mb-0">GenAI Metrics</h1>
                <div className="d-flex align-items-center gap-2">
                    <label className="mb-0 me-2 text-muted small">Window:</label>
                    {[7, 30, 90].map(d => (
                        <button
                            key={d}
                            className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setDays(d)}
                        >
                            {d}d
                        </button>
                    ))}
                    <button
                        className={`btn btn-sm ${days === 365 ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setDays(365)}
                    >
                        All
                    </button>
                </div>
            </div>

            {/* Summary row */}
            <Row className="mb-4 g-3">
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <Card.Title className="text-muted small">Total jobs (w/ metrics)</Card.Title>
                            <h2>{total_jobs}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100 border-success">
                        <Card.Body>
                            <Card.Title className="text-muted small">Completed</Card.Title>
                            <h2 className="text-success">{completed}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100 border-danger">
                        <Card.Body>
                            <Card.Title className="text-muted small">Failed</Card.Title>
                            <h2 className="text-danger">{failed}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100 border-warning">
                        <Card.Body>
                            <Card.Title className="text-muted small">In Progress</Card.Title>
                            <h2 className="text-warning">{in_progress}</h2>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {total_jobs === 0 && (
                <div className="alert alert-info">
                    No jobs with metrics data found for this period. Metrics are recorded on jobs
                    created after the metrics system was deployed.
                </div>
            )}

            {total_jobs > 0 && (
                <Row className="g-4">
                    {/* Step durations */}
                    <Col md={6}>
                        <Card>
                            <Card.Header><strong>Average Step Durations</strong></Card.Header>
                            <Card.Body className="p-0">
                                {sortedSteps.length === 0 ? (
                                    <p className="p-3 text-muted">No step timing data yet.</p>
                                ) : (
                                    <Table size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Step</th>
                                                <th className="text-end">Avg duration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedSteps.map(step => (
                                                <tr key={step}>
                                                    <td>{STEP_LABELS[step] || step}</td>
                                                    <td className="text-end font-monospace">
                                                        {fmtSeconds(avg_step_durations[step])}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Image QA */}
                    <Col md={6}>
                        <Card className="mb-4">
                            <Card.Header><strong>Image QA Summary</strong></Card.Header>
                            <Card.Body>
                                <Row className="text-center g-2 mb-3">
                                    <Col>
                                        <div className="text-muted small">Jobs with QA issues</div>
                                        <h4>{image_qa.jobs_with_issues}</h4>
                                    </Col>
                                    <Col>
                                        <div className="text-muted small">Pages regenerated</div>
                                        <h4>{image_qa.total_pages_regenerated}</h4>
                                    </Col>
                                    <Col>
                                        <div className="text-muted small">Avg iterations</div>
                                        <h4>{image_qa.avg_iterations || '–'}</h4>
                                    </Col>
                                </Row>

                                {sortedCategories.length > 0 && (
                                    <>
                                        <p className="text-muted small mb-1">Error categories:</p>
                                        {sortedCategories.map(([cat, count]) => (
                                            <div key={cat} className="mb-1">
                                                <div className="d-flex justify-content-between small">
                                                    <span>{CATEGORY_LABELS[cat] || cat}</span>
                                                    <span className="text-muted">{count} ({pct(count, totalCatCount)})</span>
                                                </div>
                                                <div className="progress" style={{ height: '6px' }}>
                                                    <div
                                                        className="progress-bar"
                                                        style={{ width: pct(count, totalCatCount) }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                                {sortedCategories.length === 0 && (
                                    <p className="text-muted small">No image QA issues recorded.</p>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Story QA */}
                        <Card>
                            <Card.Header><strong>Story QA Summary</strong></Card.Header>
                            <Card.Body>
                                {story_qa.jobs_tracked === 0 ? (
                                    <p className="text-muted small">No story QA data recorded.</p>
                                ) : (
                                    <Row className="text-center g-2">
                                        <Col>
                                            <div className="text-muted small">Jobs tracked</div>
                                            <h4>{story_qa.jobs_tracked}</h4>
                                        </Col>
                                        <Col>
                                            <div className="text-muted small">Passed on 1st pass</div>
                                            <h4>
                                                {story_qa.passed_on_first_pass}/{story_qa.jobs_tracked}
                                                {' '}
                                                <Badge bg="success" className="small">
                                                    {pct(story_qa.passed_on_first_pass, story_qa.jobs_tracked)}
                                                </Badge>
                                            </h4>
                                        </Col>
                                        <Col>
                                            <div className="text-muted small">Avg iterations</div>
                                            <h4>{story_qa.avg_iterations}</h4>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
}

export default AdminMetricsScreen;
